import React, { useState, useEffect } from 'react';
import { Receipt, Calendar, Clock, ArrowRight, AlertTriangle, X } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';

// Formato venezolano
const formatMoney = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0,00';
    return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ViaticosPendientesWidget = () => {
    const [viatico, setViatico] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);
    const navigate = useNavigate();

    // Check dismiss state
    useEffect(() => {
        const today = new Date().toDateString();
        const key = `widget_dismiss_viaticos_${today}`;
        if (localStorage.getItem(key)) setDismissed(true);
    }, []);

    useEffect(() => {
        fetchMisViaticos();
    }, []);

    const fetchMisViaticos = async () => {
        try {
            const response = await axiosInstance.get('/api/viaticos/mis-pendientes');
            if (response.data.success && response.data.viatico) {
                setViatico(response.data.viatico);
            }
        } catch (error) {
            console.log('Viaticos API not available yet');
        } finally {
            setLoading(false);
        }
    };

    const handleDismiss = () => {
        if (window.confirm('¿Deseas ocultar este widget por hoy?')) {
            const today = new Date().toDateString();
            localStorage.setItem(`widget_dismiss_viaticos_${today}`, 'true');
            setDismissed(true);
        }
    };

    if (loading || !viatico || dismissed) return null;

    // Calcular días desde regreso
    const fechaRegreso = viatico.fechaRegreso ? new Date(viatico.fechaRegreso) : null;
    const hoy = new Date();
    const diasDesdeRegreso = fechaRegreso ? Math.floor((hoy - fechaRegreso) / (1000 * 60 * 60 * 24)) : 0;

    // Urgencia según días (política: legalizar en 5 días hábiles)
    let urgencia = 'teal';
    if (diasDesdeRegreso >= 5) urgencia = 'red';
    else if (diasDesdeRegreso >= 3) urgencia = 'orange';

    const colores = {
        teal: {
            bg: 'from-teal-500 to-emerald-600',
            bgLight: 'bg-teal-50',
            text: 'text-teal-600',
            border: 'border-teal-200'
        },
        orange: {
            bg: 'from-orange-500 to-amber-600',
            bgLight: 'bg-orange-50',
            text: 'text-orange-600',
            border: 'border-orange-200'
        },
        red: {
            bg: 'from-red-500 to-rose-600',
            bgLight: 'bg-red-50',
            text: 'text-red-600',
            border: 'border-red-200'
        }
    };

    const c = colores[urgencia];

    return (
        <section className={`relative rounded-2xl overflow-hidden border ${c.border} shadow-sm group`}>
            {/* Dismiss button */}
            <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 z-20 w-6 h-6 rounded-full bg-white/80 backdrop-blur-sm text-gray-400 hover:text-gray-600 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-sm"
                title="Ocultar por hoy"
            >
                <X size={14} />
            </button>
            {/* Gradient header */}
            <div className={`bg-gradient-to-r ${c.bg} px-5 py-4 text-white`}>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Receipt size={20} />
                    </div>
                    <div>
                        <h3 className="font-bold text-lg">Viáticos Pendientes</h3>
                        <p className="text-white/80 text-sm">Legalización requerida</p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className={`${c.bgLight} p-5`}>
                {/* Destino del viaje */}
                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm mb-4">
                    <div className="flex justify-between items-center">
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Viaje a</p>
                            <p className="text-xl font-bold text-gray-900 mt-1">{viatico.destino}</p>
                        </div>
                        <div className={`w-12 h-12 rounded-full ${c.bgLight} flex items-center justify-center`}>
                            <span className="text-2xl">🧳</span>
                        </div>
                    </div>
                </div>

                {/* Monto y días */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className={`flex items-center gap-2 p-3 rounded-lg ${c.bgLight}`}>
                        <Receipt size={16} className={c.text} />
                        <div>
                            <p className="text-xs text-gray-500">Monto</p>
                            <p className="font-bold text-gray-900">Bs {formatMoney(viatico.monto)}</p>
                        </div>
                    </div>
                    <div className={`flex items-center gap-2 p-3 rounded-lg ${c.bgLight}`}>
                        <Clock size={16} className={c.text} />
                        <div>
                            <p className="text-xs text-gray-500">Días transcurridos</p>
                            <p className={`font-bold ${urgencia === 'red' ? 'text-red-600' : 'text-gray-900'}`}>
                                {diasDesdeRegreso} días
                            </p>
                        </div>
                    </div>
                </div>

                {urgencia === 'red' && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-100 text-red-700 text-sm mb-4">
                        <AlertTriangle size={16} />
                        <span className="font-medium">Urgente: Excedido el plazo de legalización</span>
                    </div>
                )}

                <button
                    onClick={() => navigate('/solicitud-viaticos')}
                    className="w-full py-3 rounded-xl bg-gray-900 text-white font-medium flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors"
                >
                    Ver mis solicitudes
                    <ArrowRight size={16} />
                </button>
            </div>
        </section>
    );
};

export default ViaticosPendientesWidget;
